import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { waitlistSchema, getValidationError } from "@/lib/validation";

const WaitlistForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Honeypot field for bot detection - bots will fill this hidden field
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Bot detection: if honeypot field is filled, silently reject
    if (honeypot) {
      toast({
        title: "תודה!",
        description: "נרשמת בהצלחה לרשימת ההמתנה",
      });
      return;
    }
    
    // Validate form data with Zod schema
    const validationResult = waitlistSchema.safeParse({
      name,
      email,
      phone,
    });

    if (!validationResult.success) {
      const errorMessage = getValidationError(validationResult);
      toast({
        title: "שגיאה",
        description: errorMessage || "נא לבדוק את הפרטים שהזנת",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    const { error } = await supabase
      .from('leads')
      .insert({
        name: validationResult.data.name,
        email: validationResult.data.email,
        phone: validationResult.data.phone,
        source_page: 'waitlist'
      });

    if (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשליחת הטופס. נסה שוב.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "תודה!",
        description: "נרשמת בהצלחה לרשימת ההמתנה",
      });
      setName("");
      setEmail("");
      setPhone("");
    }

    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
      {/* Honeypot field - hidden from users, bots will fill it */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="absolute opacity-0 pointer-events-none h-0 w-0"
        aria-hidden="true"
      />
      <div>
        <label htmlFor="waitlist-name" className="sr-only">שם מלא</label>
        <Input
          id="waitlist-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="שם מלא"
          required
          className="bg-background/20 border-cream/30 text-cream placeholder:text-cream/60 h-12 text-lg backdrop-blur-sm"
          maxLength={100}
          disabled={isSubmitting}
          aria-required="true"
        />
      </div>
      <div>
        <label htmlFor="waitlist-email" className="sr-only">כתובת אימייל</label>
        <Input
          id="waitlist-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="כתובת אימייל"
          required
          className="bg-background/20 border-cream/30 text-cream placeholder:text-cream/60 h-12 text-lg backdrop-blur-sm"
          maxLength={255}
          disabled={isSubmitting}
          aria-required="true"
        />
      </div>
      <div>
        <label htmlFor="waitlist-phone" className="sr-only">מספר טלפון</label>
        <Input
          id="waitlist-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="מספר טלפון"
          required
          className="bg-background/20 border-cream/30 text-cream placeholder:text-cream/60 h-12 text-lg backdrop-blur-sm"
          maxLength={12}
          disabled={isSubmitting}
          aria-required="true"
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-14 text-lg font-bold bg-gold hover:bg-gold/90 text-navy shadow-gold-glow transition-all duration-300"
      >
        {isSubmitting ? "שולח..." : "אני רוצה שיחת ייעוץ מהירה"}
      </Button>
    </form>
  );
};

export default WaitlistForm;
